import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CellInfo } from 'react-table';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router';

import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { ListTable } from '../../../components';
import { useTitle, useActionDispatcher } from '../../../hooks/index';
import {
  deleteUserPermissionsRequest,
  getUserPermissionsListRequest,
  setUserPermissionsListDirty,
} from '../../../store/modules/user-permissions/actions';
import { selectUserPermissionsList } from '../../../store/modules/user-permissions/selectors';
import DeleteAction from '../../../components/action/DeleteAction';
import UserPermissionAction from './actions/UserPermissionAction';
import ResendInviteAction from './actions/ResendInviteAction';
import { selectLists } from '../../../store/modules/lists/selectors';
import { Filter } from '../../../types/main';
import FilterMap from './FilterBar';
import ModalTeamForm from './ModalTeamForm';
import AdvancedActionCell from '../../../components/action-cell/AdvancedActionCell';

const useStyles = makeStyles(({ colors }: Theme) => createStyles({
  name: {
    margin: '0',
    color: colors.section,
  },
  email: {
    fontSize: '0.7rem',
    color: '#666',
  },
}), { name: 'TabTeam' });


const TeamList = () => {
  useTitle('Team');
  const classes = useStyles();
  const dispatch = useDispatch();

  const { loading, data, dirty } = useSelector(selectUserPermissionsList);

  const getUserPermissionsList = useActionDispatcher(getUserPermissionsListRequest);
  const setDirty = useActionDispatcher(setUserPermissionsListDirty);
  const deleteUserPermissions = useActionDispatcher(deleteUserPermissionsRequest);
  const lists = useSelector(selectLists);
  const { url, params } = useRouteMatch<{id: string}>();
  const organizationId = parseInt(params.id, 10);


  const requiredFilters: Filter[] = useMemo(() => [
    {
      key: 'organization_id',
      value: organizationId,
    },
  ], [organizationId]);

  const filterMap = FilterMap({
    organizationId,
  });

  const addButtonHandler = useCallback(() => {
    dispatch(push(`${url}/new`));
  }, [dispatch, url]);

  return (
    <>
      <ModalTeamForm />
      <ListTable
        columns={[
          {
            id: 'ref.user.full_name_first_last',
            Header: 'Name',
            Cell: (cell: CellInfo) => (
              <>
                <p className={classes.name}>{cell.original.ref.user?.full_name_first_last}</p>
                <span className={classes.email}>
                  {cell.original.ref.email_protected}
                </span>
              </>
            ),
          },
          {
            id: 'ref.user_association',
            Header: 'Association',
            width: 160,
            Cell: (cell: CellInfo) => (
              cell.original.permissions.can_edit
                ? (
                  <UserPermissionAction
                    original={cell.original}
                    title={cell.original.ref.user_association.title}
                    list={lists.data ? lists.data['user-associations'] : null}
                    field="user_association_id"
                  />
                )
                : cell.original.ref.user_association.title
            ),
          },
          {
            id: 'ref.user_permission',
            Header: 'Permissions',
            width: 130,
            Cell: (cell: CellInfo) => (
              cell.original.permissions.can_edit
                ? (
                  <UserPermissionAction
                    title={cell.original.ref.user_permission.title}
                    original={cell.original}
                    list={lists.data ? lists.data['user-permissions'] : null}
                    field="user_permission_id"
                  />
                )
                : cell.original.ref.user_permission.title
            ),
          },
          {
            id: 'ref.user_state.title',
            Header: 'Status',
            width: 100,
          },
          {
            id: 'actions',
            accessor: 'id',
            Cell: (cell: CellInfo) => (
              <AdvancedActionCell>
                {
                  (cell.original.permissions.can_delete)
                  && <DeleteAction {...cell} onDelete={deleteUserPermissions} />
                }
                {
                  (cell.original.user_state_id !== 2 && cell.original.permissions.can_edit)
                  && <ResendInviteAction userPermissions={cell.original} />
                }
              </AdvancedActionCell>
            ),
            width: 65,
          },
        ]}
        loading={(organizationId) ? loading : true}
        data={data}
        fetch={getUserPermissionsList}
        dirty={Boolean(dirty)}
        setDirty={setDirty}
        addTitle="Add to Team"
        addButtonHandler={addButtonHandler}
        requiredFilters={requiredFilters}
        filterMap={filterMap}
      />
    </>
  );
};

export default TeamList;
